import React, { useState } from 'react'

const MonthlyRecurrenceSelector = ({ value = [], onChange, error }) => {
  const [activeTab, setActiveTab] = useState('specific') // 'specific' or 'patterns'

  // Specific days of the month (1-31)
  const monthDays = Array.from({ length: 31 }, (_, i) => i + 1)

  // Special patterns
  const specialPatterns = [
    { value: 'last', label: 'Last Day', description: 'Last day of every month' },
    { value: 'first_monday', label: 'First Monday', description: 'First Monday of every month' },
    { value: 'first_tuesday', label: 'First Tuesday', description: 'First Tuesday of every month' },
    { value: 'first_wednesday', label: 'First Wednesday', description: 'First Wednesday of every month' },
    { value: 'first_thursday', label: 'First Thursday', description: 'First Thursday of every month' },
    { value: 'first_friday', label: 'First Friday', description: 'First Friday of every month' },
    { value: 'first_saturday', label: 'First Saturday', description: 'First Saturday of every month' },
    { value: 'first_sunday', label: 'First Sunday', description: 'First Sunday of every month' },
    { value: 'second_monday', label: 'Second Monday', description: 'Second Monday of every month' },
    { value: 'second_tuesday', label: 'Second Tuesday', description: 'Second Tuesday of every month' },
    { value: 'second_wednesday', label: 'Second Wednesday', description: 'Second Wednesday of every month' },
    { value: 'second_thursday', label: 'Second Thursday', description: 'Second Thursday of every month' },
    { value: 'second_friday', label: 'Second Friday', description: 'Second Friday of every month' },
    { value: 'third_monday', label: 'Third Monday', description: 'Third Monday of every month' },
    { value: 'third_tuesday', label: 'Third Tuesday', description: 'Third Tuesday of every month' },
    { value: 'third_wednesday', label: 'Third Wednesday', description: 'Third Wednesday of every month' },
    { value: 'third_thursday', label: 'Third Thursday', description: 'Third Thursday of every month' },
    { value: 'third_friday', label: 'Third Friday', description: 'Third Friday of every month' },
    { value: 'fourth_friday', label: 'Fourth Friday', description: 'Fourth Friday of every month' },
    { value: 'last_monday', label: 'Last Monday', description: 'Last Monday of every month' },
    { value: 'last_tuesday', label: 'Last Tuesday', description: 'Last Tuesday of every month' },
    { value: 'last_wednesday', label: 'Last Wednesday', description: 'Last Wednesday of every month' },
    { value: 'last_thursday', label: 'Last Thursday', description: 'Last Thursday of every month' },
    { value: 'last_friday', label: 'Last Friday', description: 'Last Friday of every month' }
  ]

  const handleDayToggle = (day) => {
    const newValue = value.includes(day)
      ? value.filter(d => d !== day)
      : [...value, day]
    
    onChange(newValue)
  }

  const handlePresetSelect = (preset) => {
    switch (preset) {
      case 'month_start':
        onChange([1]) // 1st of month
        break
      case 'month_middle':
        onChange([15]) // 15th of month
        break
      case 'month_end':
        onChange(['last']) // Last day of month
        break
      case 'bi_monthly':
        onChange([1, 15]) // 1st and 15th
        break
      case 'first_last':
        onChange([1, 'last']) // First and last day
        break
      case 'none':
        onChange([])
        break
      default:
        break
    }
  }

  const getSelectedSummary = () => {
    if (value.length === 0) return 'No days selected'
    
    const specificDays = value.filter(v => typeof v === 'number').sort((a, b) => a - b)
    const patterns = value.filter(v => typeof v === 'string')
    
    const parts = []
    
    if (specificDays.length > 0) {
      if (specificDays.length <= 3) {
        parts.push(specificDays.map(d => `${d}${getOrdinalSuffix(d)}`).join(', '))
      } else {
        parts.push(`${specificDays.length} specific days`)
      }
    }
    
    if (patterns.length > 0) {
      patterns.forEach(pattern => {
        const patternObj = specialPatterns.find(p => p.value === pattern)
        if (patternObj) {
          parts.push(patternObj.label)
        }
      })
    }
    
    return parts.join(', ')
  }

  const getOrdinalSuffix = (num) => {
    const j = num % 10
    const k = num % 100
    if (j === 1 && k !== 11) return 'st'
    if (j === 2 && k !== 12) return 'nd'
    if (j === 3 && k !== 13) return 'rd'
    return 'th'
  }

  return (
    <div className="monthly-recurrence-selector">
      <div className="flex items-center justify-between mb-4">
        <label className="block text-sm font-medium text-gray-700">
          Monthly Recurrence Pattern
        </label>
        
        {/* Quick Presets */}
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">Quick select:</span>
          <button
            type="button"
            onClick={() => handlePresetSelect('month_start')}
            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            1st
          </button>
          <button
            type="button"
            onClick={() => handlePresetSelect('bi_monthly')}
            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            1st & 15th
          </button>
          <button
            type="button"
            onClick={() => handlePresetSelect('month_end')}
            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Last
          </button>
          <button
            type="button"
            onClick={() => handlePresetSelect('none')}
            className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            None
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          type="button"
          onClick={() => setActiveTab('specific')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'specific'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Specific Days
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('patterns')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'patterns'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Weekday Patterns
        </button>
      </div>

      {/* Specific Days Tab */}
      {activeTab === 'specific' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select specific days of the month (1-31). Note: Days that don't exist in shorter months will be skipped.
          </p>
          
          <div className="grid grid-cols-7 gap-2">
            {monthDays.map((day) => {
              const isSelected = value.includes(day)
              
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDayToggle(day)}
                  className={`
                    p-2 rounded-lg border-2 text-sm font-medium transition-all duration-200
                    ${isSelected 
                      ? 'bg-blue-500 text-white border-blue-500 transform scale-105' 
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  {day}
                </button>
              )
            })}
          </div>
          
          {/* Special "Last Day" option */}
          <div className="pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => handleDayToggle('last')}
              className={`
                px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200
                ${value.includes('last')
                  ? 'bg-purple-500 text-white border-purple-500'
                  : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                }
              `}
            >
              Last Day of Month
            </button>
          </div>
        </div>
      )}

      {/* Patterns Tab */}
      {activeTab === 'patterns' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select patterns like "First Friday" or "Last Monday" of every month.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {specialPatterns.map((pattern) => {
              const isSelected = value.includes(pattern.value)
              
              return (
                <button
                  key={pattern.value}
                  type="button"
                  onClick={() => handleDayToggle(pattern.value)}
                  className={`
                    p-3 rounded-lg border-2 text-left transition-all duration-200
                    ${isSelected
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="font-medium text-sm">{pattern.label}</div>
                  <div className={`text-xs mt-1 ${isSelected ? 'text-green-100' : 'text-gray-500'}`}>
                    {pattern.description}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Selected Summary */}
      {value.length > 0 && (
        <div className="mt-4 bg-blue-50 rounded-lg p-3">
          <div className="flex items-center">
            <div className="text-blue-500 mr-2">🗓️</div>
            <div className="text-sm text-blue-800">
              <strong>Selected pattern:</strong> {getSelectedSummary()}
            </div>
          </div>
          
          <div className="text-xs text-blue-600 mt-1">
            Template will be available on these days every month
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center">
            <div className="text-red-500 mr-2">⚠️</div>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Help text */}
      <div className="mt-4 text-xs text-gray-500">
        💡 Tip: You can combine specific days with patterns. For example, select both "1st" and "First Friday" to have the template available on both days each month.
      </div>
    </div>
  )
}

export { MonthlyRecurrenceSelector }
